import React, { useState, useEffect } from 'react';
import api from '../api';
import './Reports.css';

function Reports({ mou, canManage }) {
  const [effectiveness, setEffectiveness] = useState(null);
  const [activities, setActivities] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const prefix = mou?.prefix;

  useEffect(() => {
    if (canManage && prefix) {
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, canManage]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch effectiveness
      const effectivenessRes = await api.get(`/api/mous/${prefix}/effectiveness`);
      setEffectiveness(effectivenessRes.data);

      // Fetch activities
      const activitiesRes = await api.get(`/api/mous/${prefix}/activities`);
      setActivities(activitiesRes.data || []);

      // Fetch comments
      const commentsRes = await api.get(`/api/mous/${prefix}/comments`);
      setCommentsCount((commentsRes.data || []).length);

    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed to load MoU reports and analytics.');
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="reports-container">
        <div className="reports-header">
          <h3>📈 Reports</h3>
        </div>
        <div className="no-access">
          🔒 Reports are only accessible to Managers and MoU SPOCs.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="reports-loading">
        <div className="spinner"></div>
        <p>Loading reports and analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>⚠️ {error}</p>
        <button onClick={fetchReportData} className="btn-retry">Retry</button>
      </div>
    );
  }

  const mouData = mou?.json || mou?.data || {};
  const validFrom = mouData.mou_start_date || mouData.validity_period?.valid_from;
  const validTo = mouData.mou_end_date || mouData.validity_period?.valid_to;
  const daysRemaining = validTo ? Math.ceil((new Date(validTo) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const isActive = daysRemaining > 0;

  const score = effectiveness?.score ?? 0;
  const label = effectiveness?.label ?? 'Under-utilized';
  const metrics = effectiveness?.metrics ?? {};

  // Score category class
  const getScoreClass = (val) => {
    if (val >= 70) return 'score-active';
    if (val >= 40) return 'score-moderate';
    return 'score-underutilized';
  };

  let kpiStrings = [];
  const successMetricsText = mouData.success_metrics || '';
  if (successMetricsText) {
    kpiStrings = successMetricsText
      .split(/[,;]|\sand\s/i)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  if (kpiStrings.length === 0) {
    kpiStrings = mouData.kpis || mouData.key_highlights || [];
  }

  // --- Recommendations Engine ---
  const generateRecommendations = () => {
    const recs = [];

    // 1. Expiry Check
    if (isActive && daysRemaining < 90) {
      recs.push({
        type: 'critical',
        text: `⏳ MoU expires in ${daysRemaining} days. Initiate renewal discussions with the partner immediately to prevent a lapse in activities.`,
      });
    } else if (!isActive) {
      recs.push({
        type: 'critical',
        text: `❌ This MoU has expired. If collaboration is still desired, draft a renewal agreement ASAP.`,
      });
    }

    // 2. Effectiveness Check
    if (score < 40 && isActive) {
      recs.push({
        type: 'warning',
        text: `📉 Overall Effectiveness is critically low (${score}%). Schedule a high-level review meeting with the internal committee and the partner.`,
      });
    }

    // 3. Activity Pipeline Check
    const activePipeline = (metrics.inProgressActivities || 0) + (metrics.upcomingActivities || 0);
    if (activePipeline === 0 && isActive) {
      recs.push({
        type: 'action',
        text: `📅 No upcoming or in-progress activities. Navigate to the 'Activities' tab and schedule a new collaborative event to maintain engagement.`,
      });
    }

    // 4. KPI Lag Checks
    if (effectiveness?.kpis && effectiveness.kpis.length > 0) {
      let completedKpis = 0;

      effectiveness.kpis.forEach((kpi, idx) => {
        const planned = Number(kpi.planned) || 0;
        const completed = Number(kpi.completed) || 0;
        const kpiName = kpiStrings[idx] || `KPI ${idx + 1}`;
        const shortName = kpiName.length > 30 ? kpiName.substring(0, 30) + '...' : kpiName;

        if (planned > 0) {
          const percent = completed / planned;
          if (completed === 0) {
            recs.push({
              type: 'action',
              text: `🚀 "${shortName}" has not started (0/${planned}). Action: Schedule a kick-off meeting or assign a dedicated faculty driver.`,
            });
          } else if (percent < 0.5 && percent > 0) {
            recs.push({
              type: 'warning',
              text: `⚠️ "${shortName}" is lagging (${completed}/${planned}). Action: Accelerate activities to meet the target.`,
            });
          } else if (percent >= 1) {
            completedKpis++;
          }
        }
      });

      if (completedKpis > 0 && completedKpis === effectiveness.kpis.filter(k => (Number(k.planned) || 0) > 0).length) {
        recs.push({
          type: 'success',
          text: `🏆 Outstanding! All planned KPI targets have been achieved. Consider expanding the scope of the MoU.`,
        });
      }
    }

    // Fallback if everything is great
    if (recs.length === 0 && isActive) {
      recs.push({
        type: 'success',
        text: `🌟 The MoU is healthy and progressing nicely! Maintain current engagement strategies.`,
      });
    }

    return recs;
  };

  const recommendations = generateRecommendations();

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h3>📈 MoU Reports & Analytics</h3>
        <p className="reports-subtitle">Comprehensive insights and performance metrics</p>
      </div>

      <div className="report-dashboard-grid">
        {/* Core Effectiveness Circular Gauge */}
        <div className="report-card gauge-card" id="overall-effectiveness-card">
          <h4 className="card-title">Overall Effectiveness</h4>
          <div className="gauge-wrapper">
            <div className={`circular-progress ${getScoreClass(score)}`} style={{ '--percent': score }}>
              <div className="inner-circle">
                <span className="score-number">{score}%</span>
                <span className="score-label">{label}</span>
              </div>
            </div>
          </div>
          <p className="gauge-description">
            This score reflects the activity completion rate, recency, validity, engagement, and frequency of activities under this MoU.
          </p>
          {effectiveness?.computedAt && (
            <div className="computed-time">
              Calculated on: {new Date(effectiveness.computedAt).toLocaleString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>

        {/* Breakdown details */}
        <div className="report-card breakdown-card" id="scoring-metrics-breakdown-card">
          <h4 className="card-title">KPI Progress Breakdown</h4>
          <div className="breakdown-list">
            {effectiveness?.kpis && effectiveness.kpis.length > 0 ? (
              effectiveness.kpis.map((kpi, index) => {
                const labelStr = kpiStrings[index] || `KPI ${index + 1}`;
                const planned = Number(kpi.planned) || 0;
                const completed = Number(kpi.completed) || 0;
                const percent = planned > 0 ? Math.min(100, Math.round((completed / planned) * 100)) : 0;
                
                return (
                  <div key={index} className="breakdown-item">
                    <div className="breakdown-info">
                      <span title={labelStr}>{labelStr.length > 40 ? labelStr.substring(0, 40) + '...' : labelStr}</span>
                      <strong>{completed}/{planned}</strong>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar fill-kpis" style={{ width: `${percent}%`, backgroundColor: '#8b5cf6' }}></div>
                    </div>
                    <span className="metric-desc">{percent}% of planned target completed.</span>
                  </div>
                );
              })
            ) : (
              <div className="no-kpis-report" style={{ color: '#64748b', fontSize: '0.9rem' }}>
                <p>📈 No KPIs defined to track progress.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Strategic Action Plan */}
      <section className="report-section" id="strategic-action-plan-section">
        <h4 className="section-title">💡 Strategic Action Plan</h4>
        <div className="recommendations-list">
          {recommendations.map((rec, idx) => (
            <div key={idx} className={`recommendation-card rec-${rec.type}`}>
              {rec.text}
            </div>
          ))}
        </div>
      </section>

      <div className="report-sections">
        {/* Summary Statistics */}
        <section className="report-section" id="summary-statistics-section">
          <h4 className="section-title">📊 Summary Statistics</h4>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{isActive ? '✅ Active' : '❌ Expired'}</div>
              <div className="stat-label">Status</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{daysRemaining > 0 ? daysRemaining : 0}</div>
              <div className="stat-label">Days Remaining</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{activities.length}</div>
              <div className="stat-label">Total Activities</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{commentsCount}</div>
              <div className="stat-label">Comments</div>
            </div>
          </div>
        </section>

        {/* Activity Details Breakdown */}
        <section className="report-section" id="activity-status-metrics-section">
          <h4 className="section-title">🎯 Activity Status Metrics</h4>
          <div className="stats-grid status-metrics-grid">
            <div className="stat-card stat-completed">
              <div className="stat-value">{metrics.completedActivities || 0}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card stat-in-progress">
              <div className="stat-value">{metrics.inProgressActivities || 0}</div>
              <div className="stat-label">In Progress</div>
            </div>
            <div className="stat-card stat-upcoming">
              <div className="stat-value">{metrics.upcomingActivities || 0}</div>
              <div className="stat-label">Upcoming</div>
            </div>
            <div className="stat-card stat-cancelled">
              <div className="stat-value">{metrics.cancelledActivities || 0}</div>
              <div className="stat-label">Cancelled</div>
            </div>
          </div>
        </section>

        {/* Validity Timeline */}
        <section className="report-section" id="validity-timeline-section">
          <h4 className="section-title">📅 Validity Timeline</h4>
          <div className="timeline-info">
            <div className="timeline-item">
              <span className="timeline-label">Start Date:</span>
              <span className="timeline-value">
                {validFrom ? new Date(validFrom).toLocaleDateString('en-IN', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                }) : 'N/A'}
              </span>
            </div>
            <div className="timeline-item">
              <span className="timeline-label">End Date:</span>
              <span className="timeline-value">
                {validTo ? new Date(validTo).toLocaleDateString('en-IN', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                }) : 'N/A'}
              </span>
            </div>
          </div>
        </section>

        {/* Partner Information */}
        <section className="report-section" id="partner-information-section">
          <h4 className="section-title">🤝 Partner Information</h4>
          <div className="partner-details">
            <div className="detail-row">
              <span className="detail-label">Institution:</span>
              <span className="detail-value">{mouData.partner_name || mouData.partner_institution?.name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{mouData.mou_type || mouData.type_of_mou || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Location:</span>
              <span className="detail-value">{mouData.location || 'N/A'}</span>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        {mouData.success_metrics && (
          <section className="report-section" id="success-metrics-section">
            <h4 className="section-title">🎯 Success Metrics</h4>
            <div className="metrics-content">
              <p>{mouData.success_metrics}</p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default Reports;
